import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {
  getCurrentUser,
  fetchAuthSession,
  signIn,
  signOut,
  confirmSignIn,
  type AuthUser,
} from 'aws-amplify/auth';
import { resetCdkClient } from '../data/cdkModelsClient';

type AdminRole = 'ADMIN' | 'MODERATOR' | 'USER' | null;

interface AuthContextType {
  user: AuthUser | null;
  role: AdminRole;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isModerator: boolean;
  pendingMFA: boolean;
  login: (email: string, password: string) => Promise<void>;
  confirmMFA: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [role, setRole] = useState<AdminRole>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingMFA, setPendingMFA] = useState(false);

  // Extract user groups from Cognito token
  const extractRole = useCallback(async (): Promise<AdminRole> => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;

      if (!accessToken) {
        return null;
      }

      // Get groups from the token payload
      const groups = accessToken.payload['cognito:groups'] as string[] | undefined;

      if (!groups || groups.length === 0) {
        return 'USER';
      }

      // Admins take precedence
      if (groups.includes('Admins')) {
        return 'ADMIN';
      }

      if (groups.includes('Moderators')) {
        return 'MODERATOR';
      }

      return 'USER';
    } catch (error) {
      console.error('Error extracting role:', error);
      return 'USER';
    }
  }, []);

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const currentUser = await getCurrentUser();
      setUser(currentUser);

      const userRole = await extractRole();
      setRole(userRole);
    } catch {
      // User is not authenticated
      setUser(null);
      setRole(null);
    } finally {
      setIsLoading(false);
    }
  }, [extractRole]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Login function — detects MFA challenge
  const login = async (email: string, password: string) => {
    try {
      // Sign out any existing session first to avoid "already signed in" error
      try {
        await signOut();
      } catch {
        // Ignore sign out errors - user might not be signed in
      }

      const result = await signIn({ username: email, password });

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
        setPendingMFA(true);
        return;
      }

      resetCdkClient();
      await checkAuth();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Confirm MFA — called after login() sets pendingMFA = true
  const confirmMFA = async (code: string) => {
    try {
      await confirmSignIn({ challengeResponse: code.trim() });
      setPendingMFA(false);
      resetCdkClient();
      await checkAuth();
    } catch (error) {
      console.error('MFA confirmation error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await signOut();
      resetCdkClient();
      setUser(null);
      setRole(null);
      setPendingMFA(false);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  // Refresh session
  const refreshSession = async () => {
    await checkAuth();
  };

  const value: AuthContextType = {
    user,
    role,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: role === 'ADMIN',
    isModerator: role === 'MODERATOR' || role === 'ADMIN',
    pendingMFA,
    login,
    confirmMFA,
    logout,
    refreshSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
